import { Injectable } from '@angular/core';
import { ListUsersResponse } from '../admin.models';
import { SupabaseClientService } from './supabase-client.service';
import { AuthService } from './auth.service';
import { UserSubscription } from '../models/user-subscription.model';
import { MarketHourRow } from '../models/market-hours.model';

export type AdminDiscountCodeRow = {
  discountcode_id: string | null;
  user_id: string | null;
  plan_id: string | null;
  plan_name?: string | null;
  used: boolean | null;
  code: string | null;
  valid_from: string | null;
  valid_to: string | null;
  duration: number | null;
  applied_discount: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminUserSubscriptionRow = UserSubscription & {
  subscription_id: string;
};

export type AdminUserTotalPlReportRow = {
  user_id: string;
  email: string | null;
  display_name: string | null;
  total_pl: number | null;
  closed_cycles: number | null;
  created_at: string | null;
  last_login: string | null;
};

export type AppIssueReportRow = {
  issue_report_id: string;
  created_at: string | null;
  updated_at: string | null;
  reported_by: string;
  reporter_email: string | null;
  reporter_display_name: string | null;
  app_section: string | null;
  page_path: string | null;
  title: string | null;
  description: string | null;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'critical';
  admin_notes: string | null;
  resolved_at: string | null;
  has_screenshot: boolean;
  screenshot_mime_type: string | null;
  screenshot_captured_at: string | null;
};

export type AppIssueReportScreenshotRow = {
  screenshot_mime_type: string | null;
  screenshot_base64: string | null;
};

export type TgBroadcastHistoryRow = {
  broadcast_id: string;
  message_html: string | null;
  has_image: boolean;
  created_at: string | null;
  created_by_email: string | null;
  audience_type: 'all' | 'channel' | 'user';
  audience_label: string | null;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  pending_count: number;
};

export type TgBroadcastSubscriberDirectoryRow = {
  userid_owner: string;
  email: string | null;
  telegram_chat_id: number;
  created_at: string | null;
};

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  private _accounts: any[] = [];
  private _steps: any[] = [];
  private _pipranges: any[] = [];
  private _riskmgmts: any[] = [];
  private _tickers: any[] = [];
  private _mt5Servers: any[] = [];
  private _marketHours: MarketHourRow[] = [];

  constructor(private supabaseSrv: SupabaseClientService, private auth: AuthService) {
  }

  async userGetList(): Promise<{
    data: any | null;
    error: { message: string } | null;
  }> {
    try {
      // 1. Verifica sessione passando dal refresh centralizzato.
      const { data: { session } } = await this.auth.getSession();

      if (!session) {
        return { data: null, error: { message: 'Sessione mancante. Effettua il login.' } };
      }

      // 2. Chiama la funzione senza passare header manuali.
      // L'SDK inietterà automaticamente l'access_token corretto.
      const { data, error } = await this.supabaseSrv.client.functions.invoke<ListUsersResponse>('user-getlist');
      console.log(data);
      if (error) {
        // Se l'errore è 401, significa che il token inviato dall'SDK è rifiutato
        return { data: null, error: { message: `Errore Edge Function: ${error.message}` } };
      }

      return { data, error: null };

    } catch (err: any) {
      return { data: null, error: { message: err.message } };
    }
  }
  async userInvite(email: string): Promise<{
    data: any | null;
    error: { message: string } | null;
  }> {
    try {
      const normalizedEmail = String(email || '').trim().toLowerCase();
      if (!normalizedEmail) {
        return {
          data: null,
          error: { message: 'Email mancante.' }
        };
      }

      const { data: { session }, error: sessionError } =
        await this.supabaseSrv.client.auth.getSession();

      if (sessionError || !session?.access_token) {
        return {
          data: null,
          error: { message: 'Sessione mancante. Effettua il login.' }
        };
      }

      const { data, error } =
        await this.supabaseSrv.client.functions.invoke('user-invite', {
          body: { email: normalizedEmail },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

      if (error) {
        const edgeMessage =
          (data as any)?.error ||
          (data as any)?.message ||
          error.message;

        return {
          data: null,
          error: { message: edgeMessage }
        };
      }

      return { data, error: null };

    } catch (err: any) {
      return {
        data: null,
        error: { message: err.message }
      };
    }
  }

  async userDelete(userId: string): Promise<{
    data: any | null;
    error: { message: string } | null;
  }> {
    try {
      const normalizedUserId = String(userId || '').trim();
      if (!normalizedUserId) {
        return {
          data: null,
          error: { message: 'User id mancante.' }
        };
      }

      const { data: { session }, error: sessionError } =
        await this.supabaseSrv.client.auth.getSession();

      if (sessionError || !session?.access_token) {
        return {
          data: null,
          error: { message: 'Sessione mancante. Effettua il login.' }
        };
      }

      const { data, error } =
        await this.supabaseSrv.client.functions.invoke('user-delete', {
          body: { userId: normalizedUserId },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

      if (error) {
        return {
          data: null,
          error: { message: `Errore Edge Function: ${error.message}` }
        };
      }

      return { data, error: null };
    } catch (err: any) {
      return {
        data: null,
        error: { message: err.message }
      };
    }
  }

  async userChangeRole(userId: string, role: string): Promise<{
    data: any | null;
    error: { message: string } | null;
  }> {
    try {
      // 1. Verifica sessione passando dal refresh centralizzato.
      const { data: { session } } = await this.auth.getSession();
      console.log(role);
      if (!session) {
        return { data: null, error: { message: 'Sessione mancante. Effettua il login.' } };
      }

      // 2. Chiama la funzione senza passare header manuali.
      // L'SDK inietterà automaticamente l'access_token corretto.
      const { data, error } = await this.supabaseSrv.client.functions.invoke<any>('user-changerole', {
        body: { userId, role }
      });
      console.log(data);
      if (error) {
        // Se l'errore è 401, significa che il token inviato dall'SDK è rifiutato
        return { data: null, error: { message: `Errore Edge Function: ${error.message}` } };
      }

      return { data, error: null };

    } catch (err: any) {
      return { data: null, error: { message: err.message } };
    }
  }

  async addRole(name: string, description: string) {
    const { data, error } = await this.supabaseSrv.client.rpc('create_new_role', {
      role_name: name,
      role_description: description
    });

    if (error) throw error;
    return data;
  }
  async changeRole(userId: string, role: string) {
    const { data, error } = await this.supabaseSrv.client.rpc('admin_update_user_role', {
      target_user: userId,
      new_role: role
    });

    if (error) throw error;
    return data;
  }

  async getUsersWithRoles() {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'admin_get_users_with_roles'
    );

    if (error) throw error;
    return data;
  }

  async replaceUserRoles(userId: string, roles: string[]) {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'admin_replace_user_roles', {
      target_user: userId,
      new_roles: roles
    }
    );

    if (error) throw error;
    return data;
    //const { data, error } =
    //  await this.supabase.client.rpc('admin_replace_user_roles', {
    //    target_user: userId,
    //    new_roles: roles
    //  });

    //if (error) throw error;
    //return data;
  }
  async getAvailableRoles(): Promise<{
    roleid: string;
    name: string;
    description: string;
  }[]> {

    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'admin_get_available_roles'
      );

    if (error) throw error;
    return data ?? [];

  }

  async getUserDiscountCodes(userId: string): Promise<AdminDiscountCodeRow[]> {
    const normalizedUserId = String(userId ?? '').trim();
    if (!normalizedUserId) return [];

    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_bill_discountcodes_admin_get',
      {
        p_user_id: normalizedUserId
      }
    );

    if (error) throw error;
    return (data as AdminDiscountCodeRow[]) ?? [];
  }

  async saveUserDiscountCode(dto: AdminDiscountCodeRow): Promise<string> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_bill_discountcodes_admin_upsert',
      {
        p_discountcode_id: dto.discountcode_id ?? null,
        p_user_id: dto.user_id ?? null,
        p_plan_id: dto.plan_id ?? null,
        p_used: dto.used ?? false,
        p_code: dto.code ?? null,
        p_valid_from: dto.valid_from ?? null,
        p_valid_to: dto.valid_to ?? null,
        p_duration: dto.duration ?? null,
        p_applied_discount: dto.applied_discount ?? null
      }
    );

    if (error) throw error;
    return String(data ?? '').trim();
  }

  async deleteUserDiscountCode(discountcodeId: string): Promise<void> {
    const normalizedId = String(discountcodeId ?? '').trim();
    if (!normalizedId) return;

    const { error } = await this.supabaseSrv.client
      .schema('billing')
      .from('discountcodes')
      .delete()
      .eq('discountcode_id', normalizedId);

    if (error) throw error;
  }

  async getUserSubscriptions(userId: string): Promise<AdminUserSubscriptionRow[]> {
    const normalizedUserId = String(userId ?? '').trim();
    if (!normalizedUserId) return [];

    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_bill_usersubscriptions_admin_get',
      {
        p_user_id: normalizedUserId
      }
    );

    if (error) throw error;
    return (data as AdminUserSubscriptionRow[]) ?? [];
  }

  async getUsersTotalPlReport(): Promise<AdminUserTotalPlReportRow[]> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_admin_users_total_pl_report_get'
    );

    if (error) throw error;
    return (data as AdminUserTotalPlReportRow[]) ?? [];
  }

  async createIssueReport(dto: {
    title: string;
    description: string;
    priority?: 'low' | 'normal' | 'high' | 'critical' | null;
    app_section?: string | null;
    page_path?: string | null;
    screenshot_base64?: string | null;
    screenshot_mime_type?: string | null;
  }): Promise<string> {
    const payload = {
      title: String(dto.title ?? '').trim(),
      description: String(dto.description ?? '').trim(),
      priority: dto.priority ?? 'normal',
      app_section: dto.app_section ? String(dto.app_section).trim() : null,
      page_path: dto.page_path ? String(dto.page_path).trim() : null
    };

    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_app_issue_reports_create',
      {
        p_title: payload.title,
        p_description: payload.description,
        p_priority: payload.priority,
        p_app_section: payload.app_section,
        p_page_path: payload.page_path,
        p_screenshot_base64: dto.screenshot_base64 ?? null,
        p_screenshot_mime_type: dto.screenshot_mime_type ?? null
      }
    );

    if (error) throw error;
    return String(data ?? '').trim();
  }

  async getIssueReports(): Promise<AppIssueReportRow[]> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_app_issue_reports_admin_get'
    );

    if (error) throw error;
    return (data as AppIssueReportRow[]) ?? [];
  }

  async getMyIssueReports(): Promise<AppIssueReportRow[]> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_app_issue_reports_get_my'
    );

    if (error) throw error;
    return (data as AppIssueReportRow[]) ?? [];
  }

  async updateIssueReport(dto: {
    issue_report_id: string;
    status: 'open' | 'in_progress' | 'resolved' | 'closed';
    priority: 'low' | 'normal' | 'high' | 'critical';
    admin_notes?: string | null;
  }): Promise<void> {
    const normalizedId = String(dto.issue_report_id ?? '').trim();
    if (!normalizedId) {
      throw new Error('Issue report id is required.');
    }

    const { error } = await this.supabaseSrv.client.rpc(
      'fn_app_issue_reports_admin_upsert',
      {
        p_issue_report_id: normalizedId,
        p_status: dto.status,
        p_priority: dto.priority,
        p_admin_notes: dto.admin_notes ?? null
      }
    );

    if (error) throw error;
  }

  async getIssueReportScreenshot(issueReportId: string): Promise<AppIssueReportScreenshotRow | null> {
    const normalizedId = String(issueReportId ?? '').trim();
    if (!normalizedId) {
      return null;
    }

    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_app_issue_reports_admin_get_screenshot',
      {
        p_issue_report_id: normalizedId
      }
    );

    if (error) throw error;

    const rows = (data as AppIssueReportScreenshotRow[] | null) ?? [];
    return rows[0] ?? null;
  }

  async getTelegramSubscribersCount(): Promise<number> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_tg_broadcast_admin_subscribers_count'
    );

    if (error) throw error;
    return Number(data ?? 0);
  }

  async sendTelegramBroadcastToAll(dto: {
    message_html: string;
    image_base64?: string | null;
    image_mime_type?: string | null;
  }): Promise<{ broadcast_id: string; recipient_count: number }> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_tg_broadcast_admin_send_all',
      {
        p_message_html: dto.message_html ?? '',
        p_image_base64: dto.image_base64 ?? null,
        p_image_mime_type: dto.image_mime_type ?? null
      }
    );

    if (error) throw error;

    const rows = (data as { broadcast_id: string; recipient_count: number }[] | null) ?? [];
    if (!rows[0]) throw new Error('No response from broadcast send.');
    return rows[0];
  }

  async sendTelegramBroadcastToChannel(dto: {
    message_html: string;
    image_base64?: string | null;
    image_mime_type?: string | null;
  }): Promise<{ broadcast_id: string; recipient_count: number }> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_tg_broadcast_admin_send_channel',
      {
        p_message_html: dto.message_html ?? '',
        p_image_base64: dto.image_base64 ?? null,
        p_image_mime_type: dto.image_mime_type ?? null
      }
    );

    if (error) throw error;

    const rows = (data as { broadcast_id: string; recipient_count: number }[] | null) ?? [];
    if (!rows[0]) throw new Error('No response from broadcast send.');
    return rows[0];
  }

  async sendTelegramBroadcastToUser(dto: {
    user_id: string;
    message_html: string;
    image_base64?: string | null;
    image_mime_type?: string | null;
  }): Promise<{ broadcast_id: string; recipient_count: number }> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_tg_broadcast_admin_send_to_user',
      {
        p_userid: dto.user_id,
        p_message_html: dto.message_html ?? '',
        p_image_base64: dto.image_base64 ?? null,
        p_image_mime_type: dto.image_mime_type ?? null
      }
    );

    if (error) throw error;

    const rows = (data as { broadcast_id: string; recipient_count: number }[] | null) ?? [];
    if (!rows[0]) throw new Error('No response from broadcast send.');
    return rows[0];
  }

  async getTelegramSubscribersDirectory(): Promise<TgBroadcastSubscriberDirectoryRow[]> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_tg_broadcast_admin_subscribers_directory'
    );

    if (error) throw error;
    return (data as TgBroadcastSubscriberDirectoryRow[]) ?? [];
  }

  async getTelegramBroadcastHistory(limit = 20): Promise<TgBroadcastHistoryRow[]> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_tg_broadcast_admin_history_get',
      { p_limit: limit }
    );

    if (error) throw error;
    return (data as TgBroadcastHistoryRow[]) ?? [];
  }

  async resendTelegramBroadcast(broadcastId: string): Promise<{ broadcast_id: string; recipient_count: number }> {
    const normalizedId = String(broadcastId ?? '').trim();
    if (!normalizedId) {
      throw new Error('Broadcast id is required.');
    }

    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_tg_broadcast_admin_resend',
      { p_broadcast_id: normalizedId }
    );

    if (error) throw error;

    const rows = (data as { broadcast_id: string; recipient_count: number }[] | null) ?? [];
    if (!rows[0]) throw new Error('No response from resend operation.');
    return rows[0];
  }

  async saveUserSubscription(dto: {
    subscription_id?: string | null;
    user_id: string;
    plan_id: string;
    status: string;
    start_date?: string | null;
    end_date?: string | null;
    renews_at?: string | null;
  }): Promise<string> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_bill_usersubscriptions_admin_upsert',
      {
        p_subscription_id: dto.subscription_id ?? null,
        p_user_id: dto.user_id,
        p_plan_id: dto.plan_id,
        p_status: dto.status,
        p_start_date: dto.start_date ?? null,
        p_end_date: dto.end_date ?? null,
        p_renews_at: dto.renews_at ?? null
      }
    );

    if (error) throw error;
    return String(data ?? '').trim();
  }

  async deleteUserSubscription(subscriptionId: string): Promise<void> {
    const normalizedId = String(subscriptionId ?? '').trim();
    if (!normalizedId) return;

    const { error } = await this.supabaseSrv.client.rpc(
      'fn_bill_usersubscriptions_admin_delete',
      {
        p_subscription_id: normalizedId
      }
    );

    if (error) throw error;
  }








  async getPropFirms() {
    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_propfirms_get'
      );

    if (error) throw error;

    return data ?? [];


  }



  async savePropFirm(dto: any): Promise<string> {

    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_propfirms_upsert',
        {
          p_propfirmid: dto.propfirmid,
          p_name: dto.name,
          p_description: dto.description,
          p_site_uri: dto.site_uri,
          p_referal_uri: dto.referal_uri
        }) as {
          data: any | null;
          error: any;
        };

    if (error) throw error;

    return data!;
  }

  async getMt5Servers(): Promise<any[]> {
    if (this._mt5Servers.length) {
      return this._mt5Servers;
    }

    const { data, error } = await this.supabaseSrv.client.rpc('fn_inv_mt5_servers_get');
    if (error) throw error;

    this._mt5Servers = data ?? [];
    return this._mt5Servers;
  }

  async getMt5ServersByFirm(propfirmId: string): Promise<any[]> {
    const id = String(propfirmId ?? '').trim();
    if (!id) return [];

    const rows = await this.getMt5Servers();
    return rows.filter(x => String(x?.propfirm_id ?? '').trim() === id);
  }

  async getMt5ServersScoped(propfirmId: string | null): Promise<any[]> {
    const normalizedId = String(propfirmId ?? '').trim();
    const rows = await this.getMt5Servers();

    if (!normalizedId) {
      return rows.filter(x => !String(x?.propfirm_id ?? '').trim());
    }

    return rows.filter(x => String(x?.propfirm_id ?? '').trim() === normalizedId);
  }

  async saveMt5Server(dto: any): Promise<string> {
    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_inv_mt5_servers_upsert',
      {
        p_mt5_server_id: dto.mt5_server_id ?? null,
        p_propfirm_id: dto.propfirm_id ?? null,
        p_server: dto.server ?? null
      }
    ) as {
      data: any | null;
      error: any;
    };

    if (error) throw error;

    const id = String(data ?? '').trim();
    if (id) {
      const normalized = {
        ...dto,
        mt5_server_id: id
      };

      const idx = this._mt5Servers.findIndex(x => String(x?.mt5_server_id ?? '') === id);
      if (idx >= 0) {
        const copy = [...this._mt5Servers];
        copy[idx] = { ...copy[idx], ...normalized };
        this._mt5Servers = copy;
      } else {
        this._mt5Servers = [normalized, ...this._mt5Servers];
      }
    }

    return id;
  }

  async deleteMt5Server(mt5ServerId: string): Promise<void> {
    const id = String(mt5ServerId ?? '').trim();
    if (!id) return;

    const { error } = await this.supabaseSrv.client.rpc(
      'fn_inv_mt5_servers_delete',
      {
        p_mt5_server_id: id
      }
    );

    if (error) throw error;

    this._mt5Servers = this._mt5Servers.filter(x => String(x?.mt5_server_id ?? '').trim() !== id);
  }

  async getMarketHours(forceReload = false): Promise<MarketHourRow[]> {
    if (!forceReload && this._marketHours.length) {
      return this._marketHours;
    }

    const { data, error } = await this.supabaseSrv.client.rpc('fn_inv_market_hours_get');
    if (error) throw error;

    this._marketHours = (data as MarketHourRow[]) ?? [];
    return this._marketHours;
  }

  async saveMarketHour(dto: MarketHourRow): Promise<string> {
    const normalizedDayOfWeek = dto.day_of_week === null || dto.day_of_week === undefined || dto.day_of_week === ('' as any)
      ? null
      : Number(dto.day_of_week);

    const { data, error } = await this.supabaseSrv.client.rpc(
      'fn_inv_market_hours_upsert',
      {
        p_id: dto.id ?? null,
        p_symbol_id: dto.symbol_id ?? null,
        p_date_utc: dto.date_utc ?? null,
        p_day_of_week: Number.isFinite(normalizedDayOfWeek) ? normalizedDayOfWeek : null,
        p_open_time_utc: dto.open_time_utc ?? null,
        p_close_time_utc: dto.close_time_utc ?? null,
        p_is_closed: dto.is_closed ?? false,
        p_description: dto.description ?? null,
        p_enabled: dto.enabled ?? true
      }
    );

    if (error) throw error;

    const id = String(data ?? '').trim();
    if (id) {
      const normalized: MarketHourRow = {
        ...dto,
        id,
        day_of_week: Number.isFinite(normalizedDayOfWeek) ? normalizedDayOfWeek : null
      };

      const idx = this._marketHours.findIndex(x => String(x?.id ?? '').trim() === id);
      if (idx >= 0) {
        const copy = [...this._marketHours];
        copy[idx] = { ...copy[idx], ...normalized };
        this._marketHours = copy;
      } else {
        this._marketHours = [normalized, ...this._marketHours];
      }
    }

    return id;
  }

  async deleteMarketHour(id: string): Promise<void> {
    const normalizedId = String(id ?? '').trim();
    if (!normalizedId) return;

    const { error } = await this.supabaseSrv.client.rpc(
      'fn_inv_market_hours_delete',
      {
        p_id: normalizedId
      }
    );

    if (error) throw error;

    this._marketHours = this._marketHours.filter(x => String(x?.id ?? '').trim() !== normalizedId);
  }
  async getRiskManagement(): Promise<any[]> {

    if (this._riskmgmts.length)
      return this._riskmgmts;

    const { data: riskmgmts, error } =
      await this.supabaseSrv.client.rpc(
        'fn_riskmanagement_get'
      );

    if (error) throw error;

    this._riskmgmts = riskmgmts ?? [];

    return this._riskmgmts;
  }

  //async getRiskManagement() {
  //  const { data, error } =
  //    await this.supabaseSrv.client.rpc(
  //      'fn_riskmanagement_get'
  //    );

  //  if (error) throw error;

  //  return data ?? [];



  //}

  async deleteRiskManagement(id: string) {

    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_riskmanagement_delete',
        { p_riskmanagementid: id }
      );

    if (error) {
      console.error(error);
      return;
    }

    if (data) {
      console.log("Deleted");
    }

  }
  async saveRiskManagement(dto: any): Promise<any> {

    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_riskmanagement_upsert',
        {
          p_riskmanagementid: dto.riskmanagementid,
          p_name: dto.name,
          p_strrisk_slin_perc: dto.strrisk_slin_perc,
          p_strrisk_slto_perc: dto.strrisk_slto_perc,
          p_strrisk_tpin_perc: dto.strrisk_tpin_perc,
          p_strrisk_tpto_perc: dto.strrisk_tpto_perc
        }) as {
          data: any | null;
          error: any;
        };

    if (error) throw error;

    const saved = Array.isArray(data)
      ? data[0]
      : data;

    if (saved?.riskmanagementid) {
      const id = String(saved.riskmanagementid);
      const idx = this._riskmgmts.findIndex(x => String(x?.riskmanagementid ?? '') === id);
      if (idx >= 0) {
        const copy = [...this._riskmgmts];
        copy[idx] = saved;
        this._riskmgmts = copy;
      } else {
        this._riskmgmts = [saved, ...this._riskmgmts];
      }
    }

    return saved;
  }




  async getPipRanges(): Promise<any[]> {

    if (this._pipranges.length)
      return this._pipranges;

    const { data: pipranges, error } =
      await this.supabaseSrv.client.rpc(
        'fn_pipranges_get'
      );

    if (error) throw error;

    this._pipranges = pipranges ?? [];

    return this._pipranges;
  }

  //async getPipRanges() {

  //  const { data, error } =
  //    await this.supabaseSrv.client.rpc(
  //      'fn_pipranges_get'
  //    );

  //  if (error) throw error;

  //  return data ?? [];




  //}
  async savePipRange(dto: any): Promise<string> {

    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_pipranges_upsert',
        {
          p_piprangeid: dto.piprangeid,
          p_name: dto.name,
          p_pip_from: dto.pip_from,
          p_pip_to: dto.pip_to
        }) as {
          data: any | null;
          error: any;
        };

    if (error) throw error;

    const id = String(data ?? '').trim();
    if (id) {
      const normalized = {
        ...dto,
        piprangeid: id
      };

      const idx = this._pipranges.findIndex(x => String(x?.piprangeid ?? '') === id);
      if (idx >= 0) {
        const copy = [...this._pipranges];
        copy[idx] = { ...copy[idx], ...normalized };
        this._pipranges = copy;
      } else {
        this._pipranges = [normalized, ...this._pipranges];
      }
    }

    return data!;
  }

  async getPfAccounts(): Promise<any[]> {

    if (this._accounts.length)
      return this._accounts;

    const { data: accounts, error } =
      await this.supabaseSrv.client.rpc(
        'fn_pfaccounts_get'
      );

    if (error) throw error;

    this._accounts = accounts ?? [];

    return this._accounts;
  }


  //async getPfAccounts() {

  //  const { data, error } =
  //    await this.supabaseSrv.client.rpc(
  //      'fn_pfaccounts_get'
  //    );

  //  if (error) throw error;

  //  return data ?? [];




  //}

  async savePfAccount(dto: any): Promise<string> {

    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_pfaccounts_upsert',
        {
          p_pfaccountid: dto.pfaccountid,
          p_propfirmid: dto.propfirmid,
          p_name: dto.name,
          p_extimated_cost: dto.extimated_cost,
          p_account_size: dto.account_size,
          p_suggestedbalance_funded: dto.suggestedbalance_funded,
          p_suggestedbalance_challenge: dto.suggestedbalance_challenge
        }) as {
          data: any | null;
          error: any;
        };

    if (error) throw error;

    const id = String(data ?? '').trim();
    if (id) {
      const normalized = {
        ...dto,
        pfaccountid: id
      };

      const idx = this._accounts.findIndex(x => String(x?.pfaccountid ?? '') === id);
      if (idx >= 0) {
        const copy = [...this._accounts];
        copy[idx] = { ...copy[idx], ...normalized };
        this._accounts = copy;
      } else {
        this._accounts = [normalized, ...this._accounts];
      }
    }

    return id;
  }

  async getPfAccountSteps(): Promise<any[]> {

    if (this._steps.length)
      return this._steps;

    const { data: steps, error } =
      await this.supabaseSrv.client.rpc(
        'fn_pfaccountsteps_get'
      );

    if (error) throw error;

    this._steps = steps ?? [];

    return this._steps;
  }


  //async getPfAccountSteps() {

  //  const { data, error } =
  //    await this.supabaseSrv.client.rpc(
  //      'fn_pfaccountsteps_get'
  //    );

  //  if (error) throw error;

  //  return data ?? [];




  //}

  async savePfAccountStep(dto: any): Promise<string> {

    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_pfaccountsteps_upsert',
        {
          p_pfaccountstepsid: dto.pfaccountstepsid,
          p_name: dto.name,
          p_exec_order: dto.exec_order,
          p_funded: dto.funded,   // 🔴 ora funded
          p_rules_maximumloss_perc: dto.rules_maximumloss_perc,
          p_rules_profittarget_perc: dto.rules_profittarget_perc
        }) as {
          data: any | null;
          error: any;
        };

    if (error) throw error;

    const id = String(data ?? '').trim();
    if (id) {
      const normalized = {
        ...dto,
        pfaccountstepsid: id
      };

      const idx = this._steps.findIndex(x => String(x?.pfaccountstepsid ?? '') === id);
      if (idx >= 0) {
        const copy = [...this._steps];
        copy[idx] = { ...copy[idx], ...normalized };
        this._steps = copy;
      } else {
        this._steps = [normalized, ...this._steps];
      }
    }

    return data!;
  }

  async getTickers(): Promise<any[]> {


    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_tickers_get'
      );

    if (error) throw error;

    return data ?? [];
  }

  async saveTicker(dto: any): Promise<any> {
    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_tickers_upsert',
        {
          p_tickerid: dto.tickerid,
          p_acronym: dto.acronym,
          p_description: dto.description
        });

    if (error) throw error;

    const saved = Array.isArray(data)
      ? data[0]
      : data;

    if (saved?.tickerid) {
      const id = String(saved.tickerid);
      const idx = this._tickers.findIndex(x => String(x?.tickerid ?? '') === id);
      if (idx >= 0) {
        const copy = [...this._tickers];
        copy[idx] = saved;
        this._tickers = copy;
      } else {
        this._tickers = [saved, ...this._tickers];
      }
    }

    return saved;
  }

  async getAccountsAccountStepsList(): Promise<any[]> {

    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_pfaccounts_pfaccountsteps_get_list'
      );

    if (error) throw error;

    return data ?? [];
  }

  async saveAccountsAccountSteps(model: any, options: { includeEnabled?: boolean } = {}): Promise<any> {
    const payload: any = {
      p_pfaccounts_pfaccountstepsid: model.pfaccounts_pfaccountstepsid,
      p_pfaccountid: model.pfaccountid,
      p_pfaccountstepsid: model.pfaccountstepsid,
      p_riskmanagementid: model.riskmanagementid,
      p_piprangeid: model.piprangeid,
      p_tickerid: model.tickerid
    };

    if (options.includeEnabled) {
      payload.p_isenabled = model.isenabled;
    }

    const { data, error } =
      await this.supabaseSrv.client.rpc(
        'fn_pfaccounts_pfaccountsteps_upsert',
        payload);

    if (error) throw error;

    return Array.isArray(data)
      ? data[0]
      : data;
  }
  async deleteAccountsAccountSteps(pfaccounts_pfaccountstepsid: string): Promise<void> {

    const { error } =
      await this.supabaseSrv.client.rpc(
        'fn_pfaccounts_pfaccountsteps_delete',
        {
          p_pfaccounts_pfaccountstepsid: pfaccounts_pfaccountstepsid
        });

    if (error) throw error;
  }
}
